// OPTIMIZED ToolDetailPage.tsx - Better Ad Placements
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toolCategories } from '../data/toolsByCategory';
import { Category, Tool } from '../data/types';
import { Helmet } from 'react-helmet-async';
import slugify from '../utils/slugify';

// ONLY import necessary ad components
import MultiplexAd from '../components/Ads/MultiplexAd';
import ArticleAd from '../components/Ads/ArticleAd';

const ToolDetailPage: React.FC = () => {
  const { toolId, category } = useParams<{ toolId: string; category: string }>();
  const [tool, setTool] = useState<Tool | null>(null);
  const [matchedCategory, setMatchedCategory] = useState<Category | null>(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [adViewed, setAdViewed] = useState(false);

  useEffect(() => {
    if (toolId && category) {
      const matchedCategory = toolCategories.find(
        cat => slugify(cat.title) === category
      );
      const foundTool = matchedCategory?.tools.find(
        t => slugify(t.title) === toolId
      );
      setMatchedCategory(matchedCategory ?? null);
      setTool(foundTool ?? null);
    } else {
      setMatchedCategory(null);
      setTool(null);
    }
  }, [toolId, category]);

  // Track ad viewability
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !adViewed) {
            setAdViewed(true);
            // Track ad view in analytics
          }
        });
      },
      { threshold: 0.5 }
    );

    const adElement = document.getElementById('primary-ad');
    if (adElement) {
      observer.observe(adElement);
    }

    return () => observer.disconnect();
  }, [adViewed]);

  if (!tool) {
    return <div>Tool not found</div>;
  }

  const canonicalUrl = `https://tooliq.ai/tools/${category}/${toolId}`;
  const categoryName = matchedCategory?.title ?? 'AI Tools';
  const categorySlug = matchedCategory ? slugify(matchedCategory.title) : category;
  const categoryUrl = `/tools/${categorySlug}/`;

  return (
    <>
      <Helmet>
        <title>{`${tool.title} Review - Features, Pricing & Alternatives | TooliQ.ai`}</title>
        <meta
          name="description"
          content={`${tool.description} Read our expert review of ${tool.title}, compare features and pricing with alternatives. Updated January 2025.`}
        />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Enhanced Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": tool.title,
            "description": tool.description,
            "applicationCategory": "AI Tool",
            "operatingSystem": "All",
            "offers": {
              "@type": "Offer",
              "price": tool.isFree ? "0" : "Varies",
              "priceCurrency": "USD"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue":  4.5,
              "reviewCount":  25
            },
            "author": {
              "@type": "Organization",
              "name": "TooliQ.ai"
            }

          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-slate-600">
            <Link to="/" className="hover:text-purple-600">Home</Link>
            <span>›</span>
            <Link to="/tools" className="hover:text-purple-600">AI Tools</Link>
            <span>›</span>
            <Link to={categoryUrl} className="hover:text-purple-600">
              {categoryName}
            </Link>
            <span>›</span>
            <span className="text-slate-800 font-medium">{tool.title}</span>
          </nav>
        </div>

        <div className="max-w-7xl mx-auto px-6 pb-12">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Main Content - 2/3 width */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* Header Card */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                <div className="flex items-start gap-6">
                  {tool.icon && (
                    <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center text-3xl text-white shadow-lg flex-shrink-0">
                      {tool.icon}
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold text-slate-800 mb-3">
                      {tool.title}
                    </h1>
                    <p className="text-lg text-slate-600 leading-relaxed">
                      {tool.description}
                    </p>
                    
                    {/* Quick Info */}
                    <div className="flex flex-wrap gap-3 mt-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        tool.isFree 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {tool.isFree ? 'Free Plan Available' : 'Premium Tool'}
                      </span>
                      {tool.tags && tool.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="px-3 py-1 rounded-full text-sm bg-purple-50 text-purple-700">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* AD 1: PRIMARY AD - After header, before content */}
              {/* This is the MOST VALUABLE position */}
              <div id="primary-ad" className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                {adViewed && <MultiplexAd />}
              </div>

              {/* Key Features Section */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">
                  Key Features
                </h2>
                
                {tool.longDescription ? (
                  <div 
                    className="prose max-w-none text-slate-700"
                    dangerouslySetInnerHTML={{ __html: tool.longDescription }}
                  />
                ) : (
                  <div className="space-y-4">
                    <p className="text-slate-700 leading-relaxed">
                      {tool.description}
                    </p>
                    
                    {/* Feature list */}
                    {tool.tags && tool.tags.length > 0 && (
                      <div className="mt-6">
                        <h3 className="font-semibold text-slate-800 mb-3">Core Capabilities</h3>
                        <div className="grid md:grid-cols-2 gap-3">
                          {tool.tags.map((tag, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-slate-700">
                              <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              <span>{tag}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* AD 2: SECONDARY AD - Mid content (after 60% of content) */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                {adViewed && <ArticleAd />}

              </div>

              {/* Pricing Section */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">
                  Pricing & Plans
                </h2>
                
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Starting Price</p>
                      <p className="text-3xl font-bold text-slate-900">
                        {tool.isFree ? 'Free' : 'See Plans'}
                      </p>
                    </div>
                    {tool.toolUrl && (
                      <a
                        href={tool.toolUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-colors"
                      >
                        View Pricing →
                      </a>
                    )}
                  </div>
                  
                  <p className="text-slate-600 mt-4 text-sm">
                    {tool.isFree 
                      ? 'Free plan available with basic features. Upgrade for advanced capabilities.'
                      : 'Flexible pricing plans to match your needs. Free trial available.'
                    }
                  </p>
                </div>
              </div>

              {/* Pros & Cons */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">
                  Pros & Cons
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Pros */}
                  <div>
                    <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Pros
                    </h3>
                    <ul className="space-y-2 text-slate-700">
                      <li>• Easy to use interface</li>
                      <li>• Fast performance</li>
                      <li>• Regular updates</li>
                      {tool.isFree && <li>• Free plan available</li>}
                    </ul>
                  </div>
                  
                  {/* Cons */}
                  <div>
                    <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      Cons
                    </h3>
                    <ul className="space-y-2 text-slate-700">
                      <li>• Learning curve for advanced features</li>
                      {!tool.isFree && <li>• No free tier</li>}
                      <li>• May require stable internet</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - 1/3 width */}
            <div className="space-y-6">
              
              {/* Primary CTA */}
              <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl p-6 text-white shadow-xl sticky top-4">
                <h3 className="font-bold text-xl mb-3">Try {tool.title}</h3>
                <p className="text-white/90 text-sm mb-6">
                  Get started with {tool.isFree ? 'the free plan' : 'a free trial'} today
                </p>
                {tool.toolUrl && (
                  <a
                    href={tool.toolUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center bg-white text-purple-600 font-bold py-4 px-6 rounded-xl hover:bg-gray-100 transition-colors shadow-lg"
                  >
                    Visit Website →
                  </a>
                )}
              </div>

              {/* Quick Stats */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
                <h3 className="font-bold text-slate-800 mb-4">Quick Info</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Category</span>
                    <Link
                      to={categoryUrl}
                      className="font-medium text-purple-600 hover:text-purple-700 hover:underline text-right"
                    >
                      {categoryName}
                    </Link>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Pricing</span>
                    <span className={`font-medium ${tool.isFree ? 'text-green-600' : 'text-blue-600'}`}>
                      {tool.isFree ? 'Freemium' : 'Paid'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">User Rating</span>
                    <span className="font-medium text-slate-900">4.5/5 ⭐</span>
                  </div>
                </div>
              </div>

              {/* Related Tools */}
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
                <h3 className="font-bold text-slate-800 mb-4">Similar Tools</h3>
                <div className="space-y-3">
                  <Link 
                    to="/tools" 
                    className="block p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors"
                  >
                    <p className="font-medium text-slate-800">Browse All Tools →</p>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ToolDetailPage;
